<template>
    <v-card flat>
        <v-card-title>Client Portal Formats</v-card-title>
        <v-card-text>
            <div class="text-subtitle-1 font-weight-bold mt-2 mb-2">
                National Clients
            </div>
            <v-data-table
                :headers="headers"
                :items="nationalItems"
                :loading="loading"
                disable-pagination
                hide-default-footer
            >
                <template v-slot:[`item.fileName`]="{ item }">
                    <a
                        v-if="hasFormat(item.type)"
                        href="#"
                        @click.prevent="openFormat(item.type)"
                    >
                        {{ item.fileName }}
                    </a>
                    <span v-else>—</span>
                </template>
                <template v-slot:[`item.expiration`]="{ item }">
                    <span>{{ formatExpiration(item.expirationMonths) }}</span>
                </template>
                <template v-slot:[`item.upload`]="{ item }">
                    <v-file-input
                        v-model="files[item.type]"
                        dense
                        hide-details
                        prepend-icon=""
                        truncate-length="20"
                        :label="`Upload ${item.label}`"
                    />
                </template>
                <template v-slot:[`item.actions`]="{ item }">
                    <div class="d-flex">
                        <v-btn
                            icon
                            small
                            color="error"
                            @click="deleteFormat(item.type)"
                            :disabled="!hasFormat(item.type) || loading"
                        >
                            <v-icon>mdi-delete</v-icon>
                        </v-btn>
                        <v-btn
                            icon
                            small
                            color="primary"
                            @click="uploadFormat(item.type)"
                            :disabled="!files[item.type] || loading"
                        >
                            <v-icon>mdi-content-save</v-icon>
                        </v-btn>
                    </div>
                </template>
            </v-data-table>

            <div class="text-subtitle-1 font-weight-bold mt-6 mb-2">
                Foreign Clients
            </div>
            <v-data-table
                :headers="headers"
                :items="internationalItems"
                :loading="loading"
                disable-pagination
                hide-default-footer
            >
                <template v-slot:[`item.fileName`]="{ item }">
                    <a
                        v-if="hasFormat(item.type)"
                        href="#"
                        @click.prevent="openFormat(item.type)"
                    >
                        {{ item.fileName }}
                    </a>
                    <span v-else>—</span>
                </template>
                <template v-slot:[`item.expiration`]="{ item }">
                    <span>{{ formatExpiration(item.expirationMonths) }}</span>
                </template>
                <template v-slot:[`item.upload`]="{ item }">
                    <v-file-input
                        v-model="files[item.type]"
                        dense
                        hide-details
                        prepend-icon=""
                        truncate-length="20"
                        :label="`Upload ${item.label}`"
                    />
                </template>
                <template v-slot:[`item.actions`]="{ item }">
                    <div class="d-flex">
                        <v-btn
                            icon
                            small
                            color="error"
                            @click="deleteFormat(item.type)"
                            :disabled="!hasFormat(item.type) || loading"
                        >
                            <v-icon>mdi-delete</v-icon>
                        </v-btn>
                        <v-btn
                            icon
                            small
                            color="primary"
                            @click="uploadFormat(item.type)"
                            :disabled="!files[item.type] || loading"
                        >
                            <v-icon>mdi-content-save</v-icon>
                        </v-btn>
                    </div>
                </template>
            </v-data-table>
        </v-card-text>
    </v-card>
</template>

<script>
import { mapMutations } from 'vuex'
import { v4 as uuidv4 } from 'uuid'
import { saveFile } from '@/services/storage/saveFile'
import { storage } from '@/services/firebase'
import API from '@/services/api'

export default {
    name: 'ClientPortalFormats',
    props: {
        setting: {
            type: Object,
            required: true,
        },
    },
    data: () => ({
        loading: false,
        salesFormats: {},
        documentTypes: [],
        files: {},
        headers: [
            { text: 'Type', value: 'label', sortable: false },
            { text: 'Current file', value: 'fileName', sortable: false },
            { text: 'Expiration', value: 'expiration', sortable: false },
            { text: 'Upload', value: 'upload', sortable: false },
            { text: 'Actions', value: 'actions', sortable: false },
        ],
    }),
    computed: {
        nationalItems() {
            return this.buildItems('national')
        },
        internationalItems() {
            return this.buildItems('international')
        },
    },
    async mounted() {
        await this.loadSalesFormats()
    },
    methods: {
        ...mapMutations(['setErrorItems']),
        buildItems(clientType) {
            return (this.documentTypes || [])
                .filter(doc => doc.clientType === clientType)
                .map(doc => ({
                    type: doc.key,
                    label: doc.label,
                    expirationMonths: doc.expirationMonths,
                    fileName: this.salesFormats?.[doc.key]?.name || '',
                }))
        },
        formatExpiration(months) {
            if (months == null) return '—'
            return `${months} months`
        },
        hasFormat(type) {
            return !!(
                this.salesFormats &&
                this.salesFormats[type] &&
                this.salesFormats[type].id
            )
        },
        ensureFileSlot(type) {
            if (!Object.prototype.hasOwnProperty.call(this.files, type)) {
                this.$set(this.files, type, null)
            }
        },
        async loadSalesFormats() {
            try {
                this.loading = true
                const {
                    salesFormats,
                    documentTypes,
                } = await API.getClientSalesFormats(this.setting.id)
                this.salesFormats = salesFormats
                this.documentTypes = documentTypes
                for (const doc of documentTypes) {
                    this.ensureFileSlot(doc.key)
                }
            } catch (error) {
                this.setErrorItems({
                    source: this.$options.name,
                    message: error.message,
                })
            } finally {
                this.loading = false
            }
        },
        async uploadFormat(type) {
            try {
                const file = this.files[type]
                if (!file) return
                this.loading = true
                const ext =
                    file.name.indexOf('.') >= 0
                        ? file.name.slice(file.name.indexOf('.'))
                        : ''
                const id = uuidv4() + ext
                const path = `${JSON.parse(
                    localStorage.getItem('company')
                )}/settings/client_portal/sales_formats/${type}`
                await saveFile(file, path, id)
                const salesFormats = await API.saveClientSalesFormat({
                    settingId: this.setting.id,
                    type,
                    file: {
                        id,
                        name: file.name,
                        path,
                    },
                })
                this.salesFormats = salesFormats || {}
                this.$set(this.files, type, null)
            } catch (error) {
                this.setErrorItems({
                    source: this.$options.name,
                    message: error.message,
                })
            } finally {
                this.loading = false
            }
        },
        async openFormat(type) {
            try {
                const format = this.salesFormats?.[type]
                if (!format?.path || !format?.id) return
                this.loading = true
                const storageRef = storage()
                    .ref(format.path)
                    .child(format.id)
                const url = await storageRef.getDownloadURL()
                window.open(url, '_blank')
            } catch (error) {
                this.setErrorItems({
                    source: this.$options.name,
                    message: error.message,
                })
            } finally {
                this.loading = false
            }
        },
        async deleteFormat(type) {
            try {
                this.loading = true
                const salesFormats = await API.deleteClientSalesFormat({
                    settingId: this.setting.id,
                    type,
                })
                this.salesFormats = salesFormats || {}
            } catch (error) {
                this.setErrorItems({
                    source: this.$options.name,
                    message: error.message,
                })
            } finally {
                this.loading = false
            }
        },
    },
}
</script>
